import { DiscordSDK } from '@discord/embedded-app-sdk';
import { getEnv } from '../env.js';

/**
 * Bootstrap du SDK Discord.
 *
 * ---------------------------------------------------------------------------
 * POURQUOI IL N'Y A PLUS DE `patchUrlMappings` ICI
 * ---------------------------------------------------------------------------
 * L'activite tourne dans une iframe sandboxee derriere le proxy Discord : tout
 * appel vers un hote EXTERNE est coupe en `blocked:csp` s'il ne passe pas par
 * `/.proxy/<prefix>`. `patchUrlMappings` existait uniquement pour faire passer
 * les SDK Google (`firestore`, `identitytoolkit`, `securetoken`) a travers ce
 * proxy.
 *
 * Il n'y a plus aucun hote externe : le client ne parle qu'a sa propre origine,
 * `/api/**`, servie par Caddy sur le meme domaine que la page. La CSP de
 * l'iframe autorise l'origine du document sans le moindre mapping.
 *
 * Ne les reintroduis pas « au cas ou » : un mapping monkey-patche fetch, XHR et
 * WebSocket globalement. Reecrire des URLs same-origin ferait tomber `/api/**`
 * (et demain le WebSocket des tables) sur `/.proxy/...`, c'est-a-dire dans le
 * vide. Aucun mapping n'est necessaire tant qu'aucun tiers n'est appele.
 * ---------------------------------------------------------------------------
 */

/** Raison pour laquelle l'app ne tourne pas dans Discord. */
export type OutsideReason = 'no-frame-id' | 'missing-config' | 'sdk-error';

export interface DiscordUnavailable {
  readonly ok: false;
  readonly reason: OutsideReason;
  readonly detail: string;
}

export interface DiscordReady {
  readonly ok: true;
  readonly sdk: DiscordSDK;
}

export type DiscordBootResult = DiscordReady | DiscordUnavailable;

/**
 * Discord injecte toujours `frame_id` dans l'URL de l'iframe. Son absence veut
 * dire que la page a ete ouverte directement dans un navigateur : on le detecte
 * AVANT d'instancier le SDK, dont le constructeur leve dans ce cas.
 */
export function hasDiscordFrame(): boolean {
  return new URLSearchParams(window.location.search).get('frame_id') !== null;
}

let bootPromise: Promise<DiscordBootResult> | null = null;
let readySdk: DiscordSDK | null = null;

/**
 * Instancie le SDK et attend `ready()`.
 * Ne leve jamais : hors Discord on renvoie un resultat explicite que l'ecran de
 * boot transforme en message calme, sans boucle de retry.
 */
export function bootDiscord(): Promise<DiscordBootResult> {
  bootPromise ??= (async (): Promise<DiscordBootResult> => {
    if (!hasDiscordFrame()) {
      return {
        ok: false,
        reason: 'no-frame-id',
        detail: "Aucun `frame_id` dans l'URL : la page n'est pas chargee par Discord.",
      };
    }

    let clientId: string;
    try {
      clientId = getEnv().discordClientId;
    } catch (error) {
      return {
        ok: false,
        reason: 'missing-config',
        detail: error instanceof Error ? error.message : String(error),
      };
    }

    try {
      const sdk = new DiscordSDK(clientId);
      await sdk.ready();
      readySdk = sdk;
      return { ok: true, sdk };
    } catch (error) {
      return {
        ok: false,
        reason: 'sdk-error',
        detail: error instanceof Error ? error.message : String(error),
      };
    }
  })();
  return bootPromise;
}

/** SDK deja pret, ou null hors Discord. */
export function getSdk(): DiscordSDK | null {
  return readySdk;
}
